// Surveys list table (consistency-pass spec §4). A kore DataTable with one
// column per fact: name, status, the template it renders under, its
// all-time response count (a link to its responses), and a trailing "…"
// row menu for its verbs — Open, Results, Responses, then Delete survey,
// under the same rule as the survey page's danger zone. Rows, sort and page
// all come from `/surveys/search` (see surveys.index.tsx), so this component
// only renders — the caller owns the request and the empty state's words.
//
// Name is the one sortable column: Status and Template are filters
// (surveys-filters.ts), and the count is computed per read.

import {
    Badge,
    DataTable,
    type DataTableColumn,
    type DataTablePagination,
    type DataTableSort,
} from "@pej/kore/components";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChartLine, List, Pencil, Trash2 } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { surveyKeys } from "@/api/keys";
import type { PageRs } from "@/api/search";
import type { SurveySearchItem, Template } from "@/api/types";
import { TABLE_LABELS, tableError } from "@/components/page-states";
import { type RowActionDescriptor, RowActionsMenu } from "@/components/row-actions-menu";
import { sortableHeader } from "@/features/search/sortable-header";
import { ALL_TIME_KEY } from "@/features/time/time-chip";
import { countOf } from "@/lib/plural";
import { DeleteSurveyDialog, surveyDeleteRefusal } from "./delete-survey-dialog";

/** The Responses link opens all time, as the count counts. */
const ALL_TIME_SEARCH: Record<string, string> = { [ALL_TIME_KEY]: "all" };

export interface SurveysTableProps {
    workspaceId: string;
    surveys: SurveySearchItem[];
    /** For the Template column's name lookup — `useTemplates`'s own list;
     *  null until it loads (or if it failed), when the cell shows nothing. */
    templates: Template[] | null;
    isLoading?: boolean;
    /**
     * The request's failure, when it failed. Distinct from an empty result on
     * purpose: an empty table under a search reads "nothing matched", which
     * is a lie when what actually happened is that the search never answered.
     */
    error?: unknown;
    onRetry: () => void;
    sort?: DataTableSort;
    onSortChange?: (next: DataTableSort | undefined) => void;
    pagination?: DataTablePagination;
    /** "Nothing yet" or "nothing matches" — the caller knows which. */
    empty: { headline: string; body?: string; action?: ReactNode };
    /** Merged into kore's DataTable root. The list page passes
     *  `min-h-0 flex-1` so the table fills the viewport and scrolls its own
     *  rows; the decision stays at the page rather than being baked in here. */
    className?: string;
}

/** Why a refused delete was refused, from the list's refetched row: the
 *  mutation's onError has awaited the search refetch by the time this runs. */
function refusalFromList(queryClient: QueryClient, workspaceId: string, surveyId: string): string | undefined {
    const pages = queryClient.getQueriesData<PageRs<SurveySearchItem>>({
        queryKey: surveyKeys.searchRoot(workspaceId),
    });
    for (const [, page] of pages) {
        const fresh = page?.items.find((item) => item.id === surveyId);
        if (fresh) return surveyDeleteRefusal(fresh.responseCount);
    }
    return undefined;
}

/** Placeholder for a survey with no template assigned. */
function NoTemplate() {
    return <span className="text-muted-foreground">—</span>;
}

export function SurveysTable({
    workspaceId,
    surveys,
    templates,
    isLoading,
    error,
    onRetry,
    sort,
    onSortChange,
    pagination,
    empty,
    className,
}: SurveysTableProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [deleteTarget, setDeleteTarget] = useState<SurveySearchItem | null>(null);
    const templateName = (templateId: string | null) =>
        templateId ? (templates?.find((t) => t.id === templateId)?.name ?? null) : null;

    const actions = useMemo<RowActionDescriptor<SurveySearchItem>[]>(
        () => [
            {
                id: "open",
                label: "Open",
                icon: Pencil,
                linkProps: (s) => ({
                    to: "/w/$workspaceId/surveys/$surveyId",
                    params: { workspaceId, surveyId: s.id },
                }),
            },
            {
                id: "results",
                label: "Results",
                icon: ChartLine,
                linkProps: (s) => ({
                    to: "/w/$workspaceId/surveys/$surveyId/results",
                    params: { workspaceId, surveyId: s.id },
                }),
            },
            {
                id: "responses",
                label: "Responses",
                icon: List,
                // All time, like the count beside it.
                linkProps: (s) => ({
                    to: "/w/$workspaceId/surveys/$surveyId/responses",
                    params: { workspaceId, surveyId: s.id },
                    search: ALL_TIME_SEARCH,
                }),
            },
            {
                id: "delete",
                label: "Delete survey",
                icon: Trash2,
                section: "danger",
                disabledReason: (s) => surveyDeleteRefusal(s.responseCount),
                onClick: (s) => setDeleteTarget(s),
            },
        ],
        [workspaceId],
    );

    const columns: DataTableColumn<SurveySearchItem>[] = [
        {
            key: "Name",
            header: sortableHeader("Name", true),
            sortable: true,
            cell: (survey) => <span className="font-medium">{survey.name}</span>,
        },
        {
            key: "active",
            header: "Status",
            cell: (survey) => (
                <Badge tone={survey.active ? "success" : "neutral"}>{survey.active ? "Active" : "Inactive"}</Badge>
            ),
        },
        {
            key: "template",
            header: "Template",
            cell: (survey) => {
                // Not known yet is not "no template": nothing until the list is in.
                if (survey.templateId && !templates) return null;
                const name = templateName(survey.templateId);
                return name ? <span>{name}</span> : <NoTemplate />;
            },
        },
        {
            key: "responses",
            header: "Responses",
            align: "right",
            cell: (survey) =>
                survey.responseCount === 0 ? (
                    // A zero is muted text, not a link (nothing to open).
                    <span className="tabular-nums text-kore-text-muted">0</span>
                ) : (
                    // All-time, like the count. The row itself opens the survey
                    // on click and on Enter/Space (DataTable's onKeyDown, which
                    // preventDefaults): stopped here for both, so the link wins.
                    <Link
                        to="/w/$workspaceId/surveys/$surveyId/responses"
                        params={{ workspaceId, surveyId: survey.id }}
                        search={ALL_TIME_SEARCH}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                        className="tabular-nums hover:underline"
                        aria-label={countOf(survey.responseCount, "response")}
                    >
                        {survey.responseCount}
                    </Link>
                ),
        },
        {
            key: "actions",
            header: "",
            align: "right",
            cell: (survey) => <RowActionsMenu actions={actions} entity={survey} label={`Actions for ${survey.name}`} />,
        },
    ];

    return (
        <>
            <DataTable
                className={className}
                // py-2 rows, not kore's default py-3 — one density for every
                // table on the platform; the library default is the roomier one.
                density="compact"
                caption="Surveys"
                columns={columns}
                rows={surveys}
                rowKey={(survey) => survey.id}
                // Error wins over loading: a retry leaves the previous failure on
                // screen while it flies, and "loading" would blank the message
                // the user is deciding about.
                state={error !== undefined ? "error" : isLoading ? "loading" : undefined}
                error={tableError(error, onRetry)}
                labels={TABLE_LABELS}
                sort={sort}
                onSortChange={onSortChange}
                pagination={pagination}
                empty={empty}
                onRowClick={(survey) =>
                    void navigate({
                        to: "/w/$workspaceId/surveys/$surveyId",
                        params: { workspaceId, surveyId: survey.id },
                    })
                }
            />
            {deleteTarget && (
                <DeleteSurveyDialog
                    key={deleteTarget.id}
                    workspaceId={workspaceId}
                    survey={deleteTarget}
                    open
                    onOpenChange={(open) => !open && setDeleteTarget(null)}
                    onDeleted={() => setDeleteTarget(null)}
                    // A refusal means a response or placement landed since load;
                    // say it as the row menu now does.
                    refusalAfterConflict={() => refusalFromList(queryClient, workspaceId, deleteTarget.id)}
                />
            )}
        </>
    );
}
